<!-- src/views/bsfp/BsfpScheduleView.vue -->
<template>
  <section class="page">
    <div class="header">
      <h2 class="page__title">{{ $t('bsfp.titles.schedule') }}</h2>
    </div>

    <div class="controls-panel">
      <!-- Выбор периода -->
      <div class="control-group">
      </div>

      <button @click="saveSchedule" class="btn-save">
        💾 {{ $t('common.save') }}
      </button>
    </div>

    <!-- Сайдбар групп + таблица расписания -->
    <div class="schedule-container">
      <div class="schedule-layout">
        <aside class="groups-sidebar">
          <button
            v-for="group in groups"
            :key="group.id"
            type="button"
            class="sidebar-group-btn"
            :class="{ active: selectedSidebarGroupId === group.id }"
            @click="selectSidebarGroup(group.id)"
          >
            {{ group.name }}
          </button>

          <button type="button" class="sidebar-add-btn" @click="openCreateGroupModal">
            +
          </button>

          <button
            type="button"
            class="sidebar-remove-btn"
            :disabled="!groups.length"
            @click="openDeleteGroupModal"
          >
            -
          </button>
        </aside>

        <div class="schedule-wrapper">
          <div class="schedule-header">
            <div class="header-date">
              {{ startDate }} — {{ endDate }}
            </div>
            <div class="nav-buttons">
              <button class="btn-nav" disabled>{{ $t('bsfp.previousWeek') }}</button>
              <button class="btn-nav" disabled>{{ $t('bsfp.nextWeek') }}</button>
            </div>
          </div>

          <table class="schedule-table">
            <thead>
              <tr>
                <th class="col-time">{{ $t('bsfp.time') }}</th>
                <th v-for="day in days" :key="day" class="col-day">
                  <div class="day-header">
                    <div class="day-number">{{ formatDay(day) }}</div>
                    <div class="day-name">{{ getDayShortName(day) }}</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <!-- Временные слоты -->
              <tr v-for="timeSlot in timeSlots" :key="timeSlot" class="time-row">
                <td class="col-time">
                  <div class="time-slot">{{ timeSlot }}</div>
                </td>
                <td v-for="day in days" :key="day" class="col-day">
                  <div
                    class="cell-content"
                    @click="onCellClick(timeSlot, day)"
                    @contextmenu.prevent="onCellRightClick(timeSlot, day)"
                  >
                    {{ getGroupName(timeSlot, day) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Модал создания группы -->
    <div v-if="showCreateGroupModal" class="modal-overlay" @click.self="closeCreateGroupModal">
      <div class="modal" @click.stop>
        <h3 class="modal-title">{{ $t('bsfp.createGroup') }}</h3>

        <div class="modal-content">
          <div class="form-row">
            <label class="control-label">{{ $t('bsfp.groupName') }}</label>
            <input v-model.trim="newGroupName" type="text" class="text-input" :placeholder="$t('bsfp.groupNamePh')" />
          </div>

          <div class="form-row">
            <label class="control-label">{{ $t('bsfp.participants') }}</label>
            <div class="users-box">
              <div v-if="usersLoading" class="users-loading">{{ $t('common.loading') }}</div>
              <label v-for="u in users" :key="u.id" class="user-item">
                <input type="checkbox" :value="u.id" v-model="newGroupStudentIds" />
                <span class="user-name">{{ u.full_name }}</span>
                <span v-if="u.personal_number" class="user-pn">({{ u.personal_number }})</span>
                <span v-if="getUserExistingGroups(u.id)" class="user-warning">
                  {{ $t('bsfp.alreadyInGroup', { names: getUserExistingGroups(u.id) }) }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-cancel" @click="closeCreateGroupModal">{{ $t('common.cancel') }}</button>
          <button type="button" class="btn-confirm" :disabled="savingGroup" @click="createGroup">
            {{ savingGroup ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Модал удаления группы -->
    <div v-if="showDeleteGroupModal" class="modal-overlay" @click.self="closeDeleteGroupModal">
      <div class="modal" @click.stop>
        <h3 class="modal-title">{{ $t('bsfp.deleteGroup') }}</h3>

        <div class="modal-content">
          <div class="form-row">
            <label class="control-label">{{ $t('bsfp.group') }}</label>
            <select v-model="deleteGroupId" class="group-select">
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
          <p class="danger-hint">
            {{ $t('bsfp.deleteWarn') }}
          </p>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-cancel" @click="closeDeleteGroupModal">{{ $t('common.cancel') }}</button>
          <button type="button" class="btn-danger" :disabled="deletingGroup || !deleteGroupId" @click="confirmDeleteGroup">
            {{ deletingGroup ? $t('common.deleting') : $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Модал подтверждения очистки ячейки -->
    <div v-if="showClearCellModal" class="modal-overlay" @click.self="closeClearCellModal">
      <div class="modal" @click.stop>
        <h3 class="modal-title">{{ $t('bsfp.clearCellTitle') }}</h3>

        <div class="modal-content">
          <p class="modal-info"><strong>{{ $t('bsfp.cellGroup') }}</strong> {{ clearCellGroupName }}</p>
          <p class="modal-info"><strong>{{ $t('bsfp.cellTime') }}</strong> {{ clearCellTimeSlot }}</p>
          <p class="modal-info"><strong>{{ $t('bsfp.cellDate') }}</strong> {{ formatDisplayDate(clearCellDate) }}</p>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-cancel" @click="closeClearCellModal">{{ $t('common.cancel') }}</button>
          <button type="button" class="btn-danger" @click="confirmClearCell">{{ $t('common.clear') }}</button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <transition name="toast-slide">
      <div v-if="showToast" class="toast" :class="toastMessage.includes('✗') ? 'toast-error' : 'toast-success'">
        <div class="toast-content">
          {{ toastMessage }}
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { http } from '@/api/http'
import { getUsers } from '@/api/user'

export default {
  name: 'BsfpScheduleView',
  
  data() {
    return {
      startDate: '',
      endDate: '',
      groups: [],
      selectedSidebarGroupId: null,
      showCreateGroupModal: false,
      newGroupName: '',
      newGroupStudentIds: [],
      users: [],
      usersLoading: false,
      savingGroup: false,
      membershipIndex: {},
      showDeleteGroupModal: false,
      deleteGroupId: null,
      deletingGroup: false,
      showClearCellModal: false,
      clearCellKey: '',
      clearCellTimeSlot: '',
      clearCellDate: '',
      clearCellGroupName: '',
      days: [],
      timeSlots: [
        '08:00 - 09:00',
        '09:10 - 10:10',
        '10:20 - 11:20',
        '11:30 - 12:30',
        '13:00 - 14:00',
        '14:10 - 15:10',
        '15:20 - 16:20'
      ],
      schedule: {}, // { "timeSlot:date": groupId|null }
      toastMessage: '',
      showToast: false
    }
  },

  mounted() {
    this.initDateRange()
    this.generateDays()
    this.loadGroups()
    this.loadSchedule()
  },
  methods: {
    initDateRange() {
      const today = new Date()
      const monday = new Date(today)
      monday.setDate(today.getDate() - today.getDay() + 1)
      
      const sunday = new Date(monday)
      sunday.setDate(monday.getDate() + 6)
      
      this.startDate = this.formatDateForInput(monday)
      this.endDate = this.formatDateForInput(sunday)
      this.generateDays()
    },

    generateDays() {
      this.days = []
      const start = new Date(this.startDate)
      const end = new Date(this.endDate)
      
      while (start <= end) {
        this.days.push(this.formatDateForInput(new Date(start)))
        start.setDate(start.getDate() + 1)
      }
    },

    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatDisplayDate(dateString) {
      const date = new Date(dateString + 'T00:00:00')
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}`
    },

    formatDay(dateString) {
      const date = new Date(dateString + 'T00:00:00')
      return String(date.getDate()).padStart(2, '0')
    },

    getDayShortName(dateString) {
      const date = new Date(dateString + 'T00:00:00')
      const days = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
      return days[date.getDay()]
    },

    previousWeek() {
      const start = new Date(this.startDate)
      start.setDate(start.getDate() - 7)
      const end = new Date(this.endDate)
      end.setDate(end.getDate() - 7)
      
      this.startDate = this.formatDateForInput(start)
      this.endDate = this.formatDateForInput(end)
      this.generateDays()
    },

    nextWeek() {
      const start = new Date(this.startDate)
      start.setDate(start.getDate() + 7)
      const end = new Date(this.endDate)
      end.setDate(end.getDate() + 7)
      
      this.startDate = this.formatDateForInput(start)
      this.endDate = this.formatDateForInput(end)
      this.generateDays()
    },

    getGroupName(timeSlot, day) {
      const key = `${timeSlot}:${day}`
      const groupId = this.schedule[key]
      if (!groupId) return '+'
      const group = this.groups.find(g => g.id === groupId)
      return group ? group.name : '+'
    },

    selectSidebarGroup(groupId) {
      this.selectedSidebarGroupId = groupId
    },

    openDeleteGroupModal() {
      this.showDeleteGroupModal = true
      this.deleteGroupId = this.groups[0]?.id || null
    },

    closeDeleteGroupModal() {
      this.showDeleteGroupModal = false
      this.deleteGroupId = null
    },

    async confirmDeleteGroup() {
      const groupId = Number(this.deleteGroupId)
      if (!groupId) return

      this.deletingGroup = true
      try {
        await http.delete(`attendance/groups/${groupId}/`)

        this.groups = this.groups.filter(g => g.id !== groupId)
        this.rebuildMembershipIndex()
        Object.keys(this.schedule || {}).forEach(key => {
          if (this.schedule[key] === groupId) {
            this.schedule[key] = null
          }
        })

        if (this.selectedSidebarGroupId === groupId) {
          this.selectedSidebarGroupId = this.groups[0]?.id || null
        }

        this.closeDeleteGroupModal()
        this.showSuccessToast(this.$t('bsfp.toast.groupDeleted'))
      } catch (error) {
        console.error('Delete group error:', error)
        this.showErrorToast(this.$t('bsfp.toast.groupDeleteError'))
      } finally {
        this.deletingGroup = false
      }
    },

    onCellClick(timeSlot, day) {
      const key = `${timeSlot}:${day}`
      if (this.selectedSidebarGroupId) {
        this.schedule[key] = this.selectedSidebarGroupId
        return
      }

      this.showErrorToast(this.$t('bsfp.toast.pickGroup'))
    },

    getUserExistingGroups(userId) {
      const key = String(userId)
      const names = this.membershipIndex?.[key]
      if (!names || names.length === 0) return ''
      return names.join(', ')
    },

    rebuildMembershipIndex() {
      const index = {}
      ;(this.groups || []).forEach(g => {
        const groupName = g?.name
        const students = Array.isArray(g?.students) ? g.students : []
        students.forEach(s => {
          const sid = s?.id
          if (!sid || !groupName) return
          const k = String(sid)
          if (!index[k]) index[k] = []
          index[k].push(groupName)
        })
      })
      this.membershipIndex = index
    },

    onCellRightClick(timeSlot, day) {
      const key = `${timeSlot}:${day}`
      const groupId = this.schedule?.[key]
      if (!groupId) return

      const group = this.groups.find(g => g.id === groupId)
      this.clearCellKey = key
      this.clearCellTimeSlot = timeSlot
      this.clearCellDate = day
      this.clearCellGroupName = group?.name || '—'
      this.showClearCellModal = true
    },

    closeClearCellModal() {
      this.showClearCellModal = false
      this.clearCellKey = ''
      this.clearCellTimeSlot = ''
      this.clearCellDate = ''
      this.clearCellGroupName = ''
    },

    confirmClearCell() {
      if (this.clearCellKey) {
        this.schedule[this.clearCellKey] = null
      }
      this.closeClearCellModal()
    },

    async loadUsers() {
      this.usersLoading = true
      try {
        this.users = await getUsers({ is_archived: false })
      } catch (e) {
        console.error('Load users error:', e)
        this.users = []
      } finally {
        this.usersLoading = false
      }
    },

    openCreateGroupModal() {
      this.showCreateGroupModal = true
      this.newGroupName = ''
      this.newGroupStudentIds = []
      if (!this.users.length) {
        this.loadUsers()
      }
    },

    closeCreateGroupModal() {
      this.showCreateGroupModal = false
      this.newGroupName = ''
      this.newGroupStudentIds = []
    },

    async createGroup() {
      if (!this.newGroupName) {
        this.showErrorToast(this.$t('bsfp.toast.enterName'))
        return
      }
      if (!this.newGroupStudentIds.length) {
        this.showErrorToast(this.$t('bsfp.toast.pickParticipants'))
        return
      }

      this.savingGroup = true
      try {
        const payload = {
          name: this.newGroupName,
          district: 'bsfp',
          student_ids: this.newGroupStudentIds,
        }
        const { data } = await http.post('attendance/groups/', payload)
        if (data?.id) {
          this.groups.push(data)
          this.selectedSidebarGroupId = data.id
          this.rebuildMembershipIndex()
        } else {
          await this.loadGroups()
        }
        this.closeCreateGroupModal()
        this.showSuccessToast(this.$t('bsfp.toast.groupCreated'))
      } catch (error) {
        console.error('Create group error:', error)
        this.showErrorToast(this.$t('bsfp.toast.groupCreateError'))
      } finally {
        this.savingGroup = false
      }
    },

    showSuccessToast(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    },
  async loadGroups() {
    try {
      const { data } = await http.get('attendance/groups/', { params: { district: 'bsfp' } })
      this.groups = Array.isArray(data) ? data : []
      this.rebuildMembershipIndex()
      if (!this.selectedSidebarGroupId && this.groups.length > 0) {
        this.selectedSidebarGroupId = this.groups[0].id
      }
    } catch (error) {
      console.error('Load groups error:', error)
      this.groups = []
      this.rebuildMembershipIndex()
    }
  },

  async loadSchedule() {
    try {
      const { data } = await http.get('attendance/schedule/', {
        params: {
          district: 'bsfp',
          start_date: this.startDate,
          end_date: this.endDate,
        },
      })
      
      // Преобразуй ответ в формат schedule: { "timeSlot:date": groupId }
      this.schedule = {}
      ;(Array.isArray(data) ? data : []).forEach(item => {
        const key = `${item.time_slot}:${item.date}`
        this.schedule[key] = item.group
      })
    } catch (error) {
      console.error('Load schedule error:', error)
      this.schedule = {}
    }
  },

  async saveSchedule() {
    try {
      const payload = {
        group_id: this.selectedSidebarGroupId || this.groups[0]?.id || 1,
        start_date: this.startDate,
        end_date: this.endDate,
        schedule_data: this.schedule
      }
      
      const { data } = await http.post('attendance/schedule/bulk_save/', payload)

      this.showSuccessToast(`✓ ${data?.message || this.$t('bsfp.toast.saved').replace('✓ ', '')}`)
    } catch (error) {
      console.error('Save error:', error)
      this.showErrorToast(this.$t('bsfp.toast.saveError'))
    }
  },
    showErrorToast(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3500)
    }
  }
}
</script>

<style scoped>
.page {
  padding: 2rem;
  background: #f5f7fa;
  min-height: 100vh;
}

.header {
  margin-bottom: 2rem;
}

.page__title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.controls-panel {
  display: flex;
  gap: 2rem;
  align-items: flex-end;
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.date-range {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.date-input {
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
}

.date-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.separator {
  color: #cbd5e1;
  font-weight: 600;
}

.group-select {
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
  width: 200px;
  cursor: pointer;
}

.group-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-save {
  padding: 0.625rem 1.5rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-save:hover {
  background: #059669;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.schedule-container {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.schedule-layout {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  align-items: stretch;
}

.groups-sidebar {
  width: 220px;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #f8fafc;
  max-height: 70vh;
  overflow: auto;
}

.sidebar-group-btn {
  width: 100%;
  padding: 1rem 0.9rem;
  border: 1px solid #e2e8f0;
  background: white;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 0.9rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.sidebar-group-btn:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  transform: translateY(-1px);
}

.sidebar-group-btn.active {
  border-color: #3b82f6;
  background: #dbeafe;
  color: #1e40af;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.18);
}

.sidebar-add-btn {
  width: 100%;
  padding: 0.9rem 0.9rem;
  border: 2px dashed #cbd5e1;
  background: white;
  color: #475569;
  font-size: 1.75rem;
  font-weight: 800;
  border-radius: 0.9rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sidebar-add-btn:hover {
  border-color: #3b82f6;
  background: #f8fafc;
  color: #1e293b;
}

.sidebar-remove-btn {
  width: 100%;
  padding: 0.9rem 0.9rem;
  border: 2px dashed #fecaca;
  background: white;
  color: #b91c1c;
  font-size: 1.75rem;
  font-weight: 900;
  border-radius: 0.9rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.sidebar-remove-btn:hover {
  border-color: #ef4444;
  background: #fef2f2;
}

.sidebar-remove-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-input {
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: white;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.users-box {
  max-height: 280px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: #fff;
}

.users-loading {
  color: #64748b;
  font-size: 0.875rem;
}

.user-item {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.25rem 0;
  cursor: pointer;
  user-select: none;
}

.user-item:hover {
  background: #f8fafc;
  border-radius: 0.5rem;
}

.user-name {
  flex: 1;
}

.user-pn {
  color: #64748b;
  font-size: 0.875rem;
}

.user-warning {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 0.15rem 0.4rem;
  border-radius: 0.5rem;
}

.schedule-wrapper {
  overflow-x: auto;
  flex: 1;
  min-width: 0;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  padding: 1rem 1.5rem;
  border-bottom: 2px solid #cbd5e1;
}

.header-date {
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
}

.nav-buttons {
  display: flex;
  gap: 0.75rem;
}

.btn-nav {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
}

.btn-nav:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.schedule-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.schedule-table thead {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.schedule-table th {
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.75rem;
  color: #64748b;
  border-right: 1px solid #e2e8f0;
}

.schedule-table th:last-child {
  border-right: none;
}

.schedule-table td {
  padding: 0;
  border-right: 1px solid #e2e8f0;
}

.schedule-table td:last-child {
  border-right: none;
}

.col-time {
  width: 140px;
  flex-shrink: 0;
  background: #f8fafc;
  border-right: 2px solid #e2e8f0 !important;
  font-weight: 600;
}

.time-slot, .group-name {
  padding: 0.75rem 0.5rem;
  text-align: center;
  font-size: 0.85rem;
  color: #1e293b;
  font-weight: 600;
}

.col-day {
  width: 80px;
  flex-shrink: 0;
  text-align: center;
}

.day-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 0.5rem;
}

.day-number {
  font-weight: 700;
  color: #1e293b;
  font-size: 0.75rem;
}

.day-name {
  font-size: 0.65rem;
  color: #94a3b8;
  font-weight: 500;
}

.cell-content {
  height: 100%;
  padding: 0.75rem 0.5rem;
  text-align: center;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  transition: all 0.2s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  word-break: break-word;
}

.cell-content:hover {
  background: #dbeafe;
  color: #1e40af;
}

.schedule-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}

.time-row {
  background: white;
}

.time-row:hover {
  background: #eff6ff;
}

/* УДАЛИ ВСЕ СТИЛИ .group-row И .group-name */

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  text-align: center;
}

.modal-content {
  margin-bottom: 1.5rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-cancel {
  padding: 0.625rem 1rem;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  font-weight: 700;
  font-size: 0.875rem;
  cursor: pointer;
  color: #475569;
  transition: all 0.15s ease;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-confirm {
  padding: 0.625rem 1.25rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 800;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-confirm:hover {
  background: #059669;
}

.btn-confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-danger {
  padding: 0.625rem 1.25rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 900;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-danger:hover {
  background: #dc2626;
}

.btn-danger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.danger-hint {
  margin: 0;
  font-size: 0.875rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
}

.modal-info {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  color: #475569;
}

.groups-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.group-btn {
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;
  color: #1e293b;
  text-align: left;
}

.group-btn:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1e40af;
}

.group-btn.clear-btn {
  border-color: #ef4444;
  color: #dc2626;
}

.group-btn.clear-btn:hover {
  background: #fee2e2;
  border-color: #dc2626;
}

/* Toast Notifications */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 2000;
  max-width: 400px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.toast-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border-left: 4px solid #047857;
}

.toast-error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border-left: 4px solid #b91c1c;
}

.toast-content {
  flex: 1;
}

.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from {
  transform: translateX(400px);
  opacity: 0;
}

.toast-slide-leave-to {
  transform: translateX(400px);
  opacity: 0;
}

@media (max-width: 1024px) {
  .controls-panel {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .schedule-layout {
    flex-direction: column;
  }

  .groups-sidebar {
    width: 100%;
    min-width: 0;
    max-height: none;
  }

  .schedule-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .nav-buttons {
    width: 100%;
  }

  .btn-nav {
    flex: 1;
  }
}

@media (max-width: 768px) {
  .page {
    padding: 1rem;
  }

  .schedule-table {
    font-size: 0.75rem;
  }

  .col-time {
    width: 110px;
    font-size: 0.8rem;
  }

  .col-day {
    width: 70px;
  }

  .cell-content {
    padding: 0.5rem 0.25rem;
    min-height: 45px;
    font-size: 0.75rem;
  }

  .modal {
    width: 95%;
    padding: 1.5rem;
  }

  .toast {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
}
</style>